/**
 * RewriteSummaryOutput — renders the rewrite_summary step: the proposed
 * headline, the rewritten summary, the rationale, and a list of removed
 * (cut) phrases.
 */

import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";
import { BulletSection, SectionLabel } from "./shared";

export default function RewriteSummaryOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};

  return (
    <Stack spacing={2.5}>
      {o.headline && (
        <Box>
          <SectionLabel>{t("RewriteSummaryOutput.headline")}</SectionLabel>
          <Typography variant="h6" fontWeight={500} mt={0.5} sx={{ color: theme.palette.primary.main }}>
            {o.headline}
          </Typography>
        </Box>
      )}

      {o.summary && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.light, 0.04),
            borderLeft: `3px solid ${theme.palette.primary.light}`,
          }}
        >
          <Typography variant="body1" color="text.primary">
            {o.summary}
          </Typography>
        </Box>
      )}

      {o.rationale && (
        <Box>
          <SectionLabel>{t("RewriteSummaryOutput.whyThisWorks")}</SectionLabel>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {o.rationale}
          </Typography>
        </Box>
      )}

      <BulletSection label={t("RewriteSummaryOutput.removed")} items={o.removed} color={theme.palette.error.dark} />

      <LessonMarkdown>{step.narrative_markdown ?? ""}</LessonMarkdown>
    </Stack>
  );
}
